/**
 * P-10d (D-79 §3, §5) + SEC-2b (D-92, D-104) — the work-logs page by role.
 * Pure, unit-tested (`src/utils/__tests__/workLogsPageModel.test.ts`).
 *
 *  * Tabs (D-104, closes Q-65; replaces the D-72 sets):
 *      OTE manager:                 שלי (default) / של אחרים / פתוחות / פעילות עכשיו
 *      Account Manager · PM · TL:   שלי / של אחרים (+ a customer filter)
 *      plain employee:              שלי / פתוחות
 *    "שלי" / "של אחרים" are a server-side narrowing (`who=mine|others`) inside
 *    what the server lets the caller see (D-92).
 *  * The two views "רישומים" / "מחזורי עבודה" stay (PRESERVE #2); the default
 *    is "מחזורי עבודה".
 *  * KPI strip — for everyone: today · this week · this month · since the
 *    start · open logs; the manager also "עובדים פעילים עכשיו" (→ the tab).
 */

export const WORKLOG_TABS = ['mine', 'others', 'open', 'active'] as const;
export type WorkLogsTab = (typeof WORKLOG_TABS)[number];

/** FU-65 (P-29): while the review queue ("לבדיקה") is on, the list holds everyone's logs, so the strip shows "הכול". */
export type ShownTab = WorkLogsTab | 'all';

/** The tab the strip marks: "הכול" while the review queue lists everyone, else the chosen tab. */
export function shownTab(tab: WorkLogsTab, reviewMode: boolean): ShownTab {
  return reviewMode ? 'all' : tab;
}

/** The tabs the strip offers: the role's tabs, plus "הכול" in front while the review queue is on. */
export function stripTabs(role: WorkLogsRole, reviewMode: boolean): ShownTab[] {
  return reviewMode ? ['all', ...tabsForRole(role)] : tabsForRole(role);
}

/** D-104 — manager = OTE general manager; lead = sees others' logs (AM / PM / TL); employee = own only. */
export type WorkLogsRole = 'manager' | 'lead' | 'employee';

export type WorkLogsView = 'logs' | 'records';

/** The page role: the GM claim, else what the server says (`sees_others`, D-92). */
export function workLogsRole(isManager: boolean, seesOthers: boolean | undefined): WorkLogsRole {
  if (isManager) return 'manager';
  return seesOthers ? 'lead' : 'employee';
}

/** The tabs a role sees, in order (D-104). */
export function tabsForRole(role: WorkLogsRole): WorkLogsTab[] {
  if (role === 'manager') return ['mine', 'others', 'open', 'active'];
  if (role === 'lead') return ['mine', 'others'];
  return ['mine', 'open'];
}

/** The customer filter (D-92 / D-104) is offered to whoever sees others' logs. */
export function hasCustomerOrgFilter(role: WorkLogsRole): boolean {
  return role !== 'employee';
}

/** The tab the URL asks for, if the role has it; otherwise "שלי" (the default). */
export function effectiveTab(param: string | null | undefined, role: WorkLogsRole): WorkLogsTab {
  const tabs = tabsForRole(role);
  return (tabs as string[]).includes(param ?? '') ? (param as WorkLogsTab) : 'mine';
}

/** `?view=` → the view; absent (or anything else) is "מחזורי עבודה" (D-79 §3). */
/** C-13 (clarity, A-10): the default view is "לפי רישום"; "לפי משימה" is `?view=records` (both kept). */
export function viewFromParam(param: string | null | undefined): WorkLogsView {
  return param === 'records' ? 'records' : 'logs';
}

/**
 * What a tab narrows: "שלי" = my own logs, "של אחרים" = the other employees'
 * logs I may see, "פתוחות" = open work cycles only (in my whole scope).
 * "פעילות עכשיו" narrows nothing (it is its own panel).
 */
export function tabScope(tab: ShownTab): { who: 'mine' | 'others' | undefined; openOnly: boolean } {
  return {
    who: tab === 'mine' || tab === 'others' ? tab : undefined,
    openOnly: tab === 'open',
  };
}

export interface PageFilters {
  from: string;
  to: string;
  customerId: string;
  /** D-104 — the customer (organization) filter of the "של אחרים" views. */
  organizationId?: string;
  projectId: string;
  engagementId: string;
  milestoneId: string;
  ticketId: string;
  agentId: string;
}

/** The work-cycles request: the page filters + the tab (an open tab forces status=open). */
export function workRecordQueryParams(
  f: PageFilters,
  tab: WorkLogsTab,
  recordsStatus: '' | 'open' | 'closed',
  limit = 100,
) {
  const scope = tabScope(tab);
  const status = scope.openOnly ? 'open' : recordsStatus || undefined;
  return {
    status: status as 'open' | 'closed' | undefined,
    start_date: f.from || undefined,
    end_date: f.to || undefined,
    customer_id: f.customerId || undefined,
    organization_id: f.organizationId || undefined,
    project_id: f.projectId || undefined,
    engagement_id: f.engagementId || undefined,
    milestone_id: f.milestoneId || undefined,
    ticket_id: f.ticketId || undefined,
    agent_id: f.agentId || undefined,
    who: scope.who,
    limit,
  };
}

/** The KPI request: the page's non-period filters + the tab's "who". */
export function kpiQueryParams(f: PageFilters, tab: ShownTab) {
  const scope = tabScope(tab);
  return {
    agent_id: f.agentId || undefined,
    customer_id: f.customerId || undefined,
    organization_id: f.organizationId || undefined,
    project_id: f.projectId || undefined,
    engagement_id: f.engagementId || undefined,
    milestone_id: f.milestoneId || undefined,
    ticket_id: f.ticketId || undefined,
    who: scope.who,
  };
}

export type KpiKey = 'today' | 'week' | 'month' | 'total' | 'open' | 'activeNow';

export interface KpiSpec {
  key: KpiKey;
  /** Minutes (the four periods) or a count. */
  kind: 'minutes' | 'count';
  value: number;
}

/** The cells of the strip, in order (D-79 §5). */
export function kpiSpecs(
  k: { today_minutes?: number; week_minutes?: number; month_minutes?: number; total_minutes?: number; open_records?: number; active_now?: number } | null | undefined,
  isManager: boolean,
): KpiSpec[] {
  const n = (v: number | undefined) => Number(v ?? 0) || 0;
  // C-13 (clarity, A-10): four numbers at most — today · week · month · open. "מתחילת העבודה" and the
  // manager's "עובדים עכשיו" left the line (the "פעילות עכשיו" tab still shows who works now).
  void isManager;
  const cells: KpiSpec[] = [
    { key: 'today', kind: 'minutes', value: n(k?.today_minutes) },
    { key: 'week', kind: 'minutes', value: n(k?.week_minutes) },
    { key: 'month', kind: 'minutes', value: n(k?.month_minutes) },
    { key: 'open', kind: 'count', value: n(k?.open_records) },
  ];
  return cells;
}

/** Minutes between `startedAt` and `now` (never negative). */
export function elapsedMinutes(startedAt: string | null | undefined, now: Date): number {
  if (!startedAt) return 0;
  const s = new Date(startedAt).getTime();
  if (Number.isNaN(s)) return 0;
  return Math.max(0, Math.floor((now.getTime() - s) / 60_000));
}

/** "1:05" (h:mm) of a running time — the "זמן רץ". */
export function runningClock(startedAt: string | null | undefined, now: Date): string {
  const m = elapsedMinutes(startedAt, now);
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
}

/**
 * The employee's own running timers for the highlighted block at the top of
 * the list (bug 98121: it showed nothing): every RUNNING timer, the oldest
 * first; suspended (legacy) rows are not "active".
 */
export function runningTimersFirst<T extends { status: string; started_at: string }>(timers: readonly T[] | null | undefined): T[] {
  return (timers ?? [])
    .filter((tm) => tm.status === 'running')
    .slice()
    .sort((a, b) => new Date(a.started_at).getTime() - new Date(b.started_at).getTime());
}

/** D-159 — rows per step of the list; "הצג עוד" adds another step. */
export const PAGE_STEP = 10;

/** The `limit` for the n-th "page" (the list is re-requested from the start, so edits and deletes stay consistent). */
export function listLimit(pages: number): number {
  return PAGE_STEP * Math.max(1, Math.floor(pages) || 1);
}

/** "הצג עוד" is offered while the server has more rows than are shown. */
export function hasMoreRows(shown: number, total: number): boolean {
  return total > shown;
}

/**
 * D-159 - a worker who sees only themselves has no "עובד" column.
 * P-26 (QX-97, D-167) - nor has the "שלי" tab of a manager / lead: it lists the
 * signed-in person's own logs only.
 */
export function showWorkerColumn(role: WorkLogsRole, tab?: ShownTab): boolean {
  if (role === 'employee') return false;
  return tab !== 'mine';
}

/**
 * P-26 (QX-97, D-167) - the tab of OTHER employees' work has a filter by employee.
 * P-29 (QX-100): and so has "פתוחות" (the open cycles of everyone in the scope), not "פעילות עכשיו".
 */
export function hasEmployeeFilter(role: WorkLogsRole, tab: ShownTab): boolean {
  return role !== 'employee' && (tab === 'others' || tab === 'open');
}

/** The employees the picker offers: OTE staff logins, by name. */
export function employeeChoices(users: ReadonlyArray<{ id: string; name?: string | null; role: string }>): Array<{ value: string; label: string }> {
  return users
    .filter((u) => u.role === 'admin' || u.role === 'agent')
    .map((u) => ({ value: u.id, label: u.name || u.id }))
    .sort((a, b) => a.label.localeCompare(b.label, 'he'));
}
